import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGetUserDataQuery } from "@shared/hooks/useGetUserDataQuery";
import {
  USER_SET,
  PLATFORM_SET_LOADING,
  usePlatformDispatch,
} from "@platform//PlatformContext";
import {
  DASHBOARD_SET,
  DASHBOARD_SET_LOADING,
  useDashboardDispatch,
} from "../features/dashboard/DashboardContext";
import {
  SUPPORT_SET,
  SUPPORT_SET_LOADING,
  useSupportDispatch,
} from "../features/support/SupportContext";
import normalizeUserData from "../features/dashboard/utils/normalizeUserData";
import { clearFirebaseLocalStorage } from "@shared/services/api/firebase";

export type Args = {
  isEnabled?: boolean;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
};

export function useGetUserData(
  {
    isEnabled = true,
    refetchOnWindowFocus = true,
    refetchOnReconnect = true,
  }: Args = {
    isEnabled: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  }
) {
  const navigate = useNavigate();

  const dashboardDispatch = useDashboardDispatch();

  const platformDispatch = usePlatformDispatch();

  const supportDispatch = useSupportDispatch();

  const getUserDataQuery = useGetUserDataQuery(
    "platform",
    normalizeUserData,
    isEnabled,
    refetchOnWindowFocus,
    refetchOnReconnect
  );

  // set isLoading = true on mount
  useEffect(() => {
    if (!isEnabled) return;

    platformDispatch({
      type: PLATFORM_SET_LOADING,
      payload: { isLoading: true },
    });

    dashboardDispatch({
      type: DASHBOARD_SET_LOADING,
      payload: { isLoading: true },
    });

    supportDispatch({
      type: SUPPORT_SET_LOADING,
      payload: { isLoading: true },
    });
  }, [supportDispatch]);

  // log out on error
  useEffect(() => {
    if (getUserDataQuery.error) {
      // navigate to logout
      navigate("/logout");
    }
  }, [getUserDataQuery.error, navigate]);

  // update state when data is fetched
  useEffect(() => {
    if (getUserDataQuery.data) {
      platformDispatch({
        type: USER_SET,
        payload: { user: getUserDataQuery.data.user },
      });

      dashboardDispatch({
        type: DASHBOARD_SET,
        payload: {
          programData: getUserDataQuery.data.programData,
          organization: getUserDataQuery.data.organization,
          program: getUserDataQuery.data.program,
          user: getUserDataQuery.data.user,
          talkjs: getUserDataQuery.data.talkjs,
          isLoading: false,
        },
      });

      supportDispatch({
        type: SUPPORT_SET,
        payload: {
          articles: getUserDataQuery.data.support?.allArticles,
          categories: getUserDataQuery.data.support?.categories,
        },
      });
    }
  }, [
    getUserDataQuery.data,
    dashboardDispatch,
    platformDispatch,
    supportDispatch,
  ]);

  return getUserDataQuery;
}

export default useGetUserData;
